import { useCallback } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import { twMerge } from 'tailwind-merge'
import { dayOfWeekJP } from '~/lib/calendar/calendar'
import { getMonth } from '~/lib/date/date'
import type { DateText } from '~/lib/graphql/scalars'
import type { FormattedDate } from '~/stores/calendar/state'
import {
  useCalendarHeight,
  useCalendarQueryStatus,
  useCalendarsByMonth,
  useChangeMonth,
  useDisplayMonth,
} from '~/stores/calendar/state'
import { css } from '~/styled-system/css'
import { Dates } from './Dates'
import { Month } from './Month'

export function Calendar() {
  const { height, calendars, slide, initial } = useCalendarComponent()
  const status = useCalendarQueryStatus()
  if (status === 'pending') {
    return <Skeleton height={height} />
  }
  if (status === 'error') {
    throw new Error('カレンダー情報を取得できませんでした。')
  }
  return (
    <Swiper modules={[Navigation]} navigation={true} onSlideChange={slide} onSwiper={initial}>
      {calendars.map((calendar) => (
        <SwiperSlide
          key={calendar[0].month}
          data-key={calendar[0].date}
          className={css({ spaceY: '1' })}
        >
          <Month dates={calendar} />
          <div className="grid w-full grid-cols-7 items-start text-sm" style={{ height: height }}>
            <WeekDayHeader />
            <Dates dates={getCalendar(calendars, calendar[0].month)} />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  )
}

function WeekDayHeader() {
  return (
    <>
      {Object.entries(dayOfWeekJP).map((day, i) => {
        return (
          <div
            key={i}
            className={twMerge(
              'pt-1 pb-2 text-center',
              day[0] === 'SUN' ? 'text-peach-dark' : '',
              day[0] === 'SAT' ? 'text-blueberry-dark' : ''
            )}
          >
            {day[1]}
          </div>
        )
      })}
    </>
  )
}

function useCalendarComponent() {
  const calendars = useCalendarsByMonth()
  const date = useDisplayMonth()
  const height = useCalendarHeight()
  const change = useChangeMonth()
  const slide = useCallback(
    (swiper: SwiperType) => {
      const current = swiper.slides[swiper.activeIndex]
      function validateKey(key: string | undefined): asserts key is string {
        if (key === undefined) {
          throw new Error('key required')
        }
      }
      validateKey(current.dataset.key)
      // 渡ってくる値は DateText 型が保証されているので型アサーション
      change(current.dataset.key as DateText)
    },
    [change]
  )
  const initial = useCallback(
    (swiper: SwiperType) => {
      if (date === undefined) {
        return
      }
      // https://github.com/ikyucom/macaron-frontend/pull/1978
      swiper.slideTo(
        calendars.findIndex((c) => c[0].month === getMonth(date)),
        0
      )
    },
    [calendars, date]
  )
  return { height, calendars, slide, initial }
}

function getCalendar(calendars: FormattedDate[][], month: number) {
  return calendars.find((calendar) => calendar[0].month === month) ?? []
}
